import React from 'react'
import { Button, ButtonShape, ButtonStyle } from '../../../components/simple/button'
import { UserSelector } from '../../../components/userSelector/userSelector'
import { makeRequest, showAxiosErrorAsToast, useRequest } from '../../../request'
import { Assessment } from '../assessmentDashboard'
import { Tooltip } from '../../../components/simple/tooltip'
import { BasicUserOrGroup } from '../../../components/userSelector/users'
import { LocalTable } from '../../../components/table'
import { DateWrapper } from '../../../components/simple/date'
import { DonutChart, PieData } from '../../../components/charts/pieChart'
import { TextInput } from '../../../components/form/textInput'
import { IconReload } from '../../../icons/library'
import { usePreview } from '../../modals/preview'
import { Toasts } from '../../../providers/toast'

type AssessmentSend = {
  id: number
  name: string
  forStudent: string
  date: string
  resend: boolean
  views: number
  failedAttempts: number
}

export const SendMessageTab: React.FC<{
  assessment: Assessment
}> = ({ assessment }) => {
  const [sendToStudents, setSendToStudents] = React.useState(false)
  const [sendToGuardians, setSendToGuardians] = React.useState(false)
  const [recipients, setRecipients] = React.useState<BasicUserOrGroup[]>([])
  const [loading, setLoading] = React.useState(false)
  const sendDisabledMessage = assessment.processed.length == 0 ? 'Make sure to process your documents first' : undefined
  const [sendInfo, setSendInfo] = React.useState<string | undefined>(undefined)
  const [search, setSearch] = React.useState('')

  const { showMessagePreview } = usePreview()

  // Function to create preview message structure for assessments
  const createPreviewMessage = React.useCallback(() => {
    // Create the assessment document message body (similar to how real assessment sends work)
    const messageBody = `Please click to see the document for: ${assessment.name}`

    // For assessments, we need to handle recipients differently than regular messages
    // If "all" is selected, we need to put real student IDs in the people array for preview
    const hasAllGroup = recipients.some((r) => r.group && (r as any).groupID === 'all')
    const realCourses = recipients.filter((r) => r.group && (r as any).groupID !== 'all').map((r) => (r as any).groupID)
    const individualPeople = recipients.filter((r) => !r.group).map((r) => r.id)

    // For preview purposes, if "all" is selected, we need to include the actual student IDs
    // Use the allGroupData if available, which contains the actual student IDs
    let allStudentIds: number[] = []
    if (hasAllGroup && assessment.allGroupData?.studentIds) {
      allStudentIds = assessment.allGroupData.studentIds
    }

    return {
      type: 'mass', // Assessment messages typically go as both email and text
      body: messageBody,
      subject: assessment.name,
      recipients: {
        courses: realCourses,
        people: [...individualPeople, ...allStudentIds],
        toParents: sendToGuardians,
        toStudents: sendToStudents,
        regardingStudent: undefined,
      },
      sharedTeachers: [],
      attachments: [], // Assessment attachments are handled differently via secure links
      draft: false,
      draftID: undefined,
      confirmReceipt: false,
      allowRepliesToShared: false,
      allow_replies: false, // Assessment messages typically don't allow replies
      massSendTypes: {
        email: true,
        text: true,
        phone: false,
      },
      recording: undefined,
      recordingURL: undefined,
      test_types: undefined,
      email: undefined,
      phoneNumber: undefined,
      language: undefined,
      message: undefined,
      time: undefined,
      emergency: undefined,
      assessment: true, // Mark this as an assessment message
    }
  }, [assessment, recipients, sendToStudents, sendToGuardians])

  const previewAssessmentMessage = React.useCallback(async () => {
    if (recipients.length === 0) {
      Toasts.showToast({ message: 'Please select recipients first', duration: 3000 })
      return
    }

    if (!sendToStudents && !sendToGuardians) {
      Toasts.showToast({ message: 'Please select whether to send to students or guardians', duration: 3000 })
      return
    }

    try {
      await showMessagePreview(createPreviewMessage())
    } catch (error) {
      showAxiosErrorAsToast(error)
    }
  }, [createPreviewMessage, recipients.length, sendToStudents, sendToGuardians, showMessagePreview])

  // Function to calculate real recipient count for assessments
  const getRealRecipientCount = (
    recipients: BasicUserOrGroup[],
    sendToStudents: boolean,
    sendToGuardians: boolean
  ): number => {
    if (recipients.length === 0 || (!sendToStudents && !sendToGuardians)) return 0

    const people = new Set<number>()

    recipients.forEach((recipient) => {
      if (recipient.group) {
        // For group recipients (classes) - handle "all" group specially
        if (recipient.groupID === 'all') {
          // For "all" group, we'll handle this at the end since we need to fetch actual guardian data
          // The accurate count will be calculated after the loop using the 'guardianIDs' from the all group data
        } else {
          // For other group recipients, use memberIDs and guardianIDs if available
          if (sendToStudents && 'memberIDs' in recipient && Array.isArray((recipient as any).memberIDs)) {
            ;(recipient as any).memberIDs.forEach((id: number) => people.add(id))
          }
          if (sendToGuardians && 'guardianIDs' in recipient && Array.isArray((recipient as any).guardianIDs)) {
            ;(recipient as any).guardianIDs.forEach((id: number) => people.add(id))
          }
        }
      } else {
        // For individual recipients - now with actual guardian data from the API
        if (sendToStudents) {
          people.add(recipient.id)
        }
        if (sendToGuardians && 'guardians' in recipient && Array.isArray((recipient as any).guardians)) {
          // Use actual guardian data from API (now available after backend fix)
          ;(recipient as any).guardians.forEach((guardian: any) => people.add(guardian.id))
        }
      }
    })

    // Special handling for "all" group - use actual guardian data when available
    const allGroup = recipients.find((r) => r.group && r.groupID === 'all')
    if (allGroup) {
      let allGroupCount = 0

      // Use actual data from assessment.allGroupData if available
      if (assessment.allGroupData) {
        if (sendToStudents && sendToGuardians) {
          // Both students and guardians - use actual counts
          allGroupCount = assessment.allGroupData.studentCount + assessment.allGroupData.guardianCount
        } else if (sendToStudents) {
          // Only students - use actual student count
          allGroupCount = assessment.allGroupData.studentCount
        } else if (sendToGuardians) {
          // Only guardians - use actual guardian count
          allGroupCount = assessment.allGroupData.guardianCount
        }
      } else {
        // Fallback to estimated counts if actual guardian data is not available
        if (sendToStudents && sendToGuardians) {
          // When both students and guardians are selected for "all" group:
          // Count students once + estimate guardians (typically 1.8-2.2 guardians per student)
          allGroupCount = assessment.processed.length + Math.round(assessment.processed.length * 2.0)
        } else if (sendToStudents) {
          // Only counting students
          allGroupCount = assessment.processed.length
        } else if (sendToGuardians) {
          // Only counting guardians - each student typically has 1-3 guardians
          // Using 2.0 as a realistic average (covers single parents, couples, and some extended families)
          allGroupCount = Math.round(assessment.processed.length * 2.0)
        }
      }
      return allGroupCount
    }

    return people.size
  }

  const { data, reload: reloadSends } = useRequest<{ sends: AssessmentSend[]; sendStats: PieData }>(
    '/frontend_api/getAssessmentSends',
    React.useMemo(() => ({ assessmentID: assessment.id }), [assessment.id])
  )
  const sends = data?.sends

  return (
    <div className="mt-2 flex min-w-[500px] flex-row gap-16 self-center">
      <div className="flex flex-col gap-2">
        <Tooltip text={sendDisabledMessage}>
          <div className={`${sendDisabledMessage ? 'opacity-50 *:pointer-events-none' : ''} flex flex-col gap-2`}>
            <UserSelector<BasicUserOrGroup>
              selectedUsers={recipients}
              setSelectedUsers={setRecipients}
              searchData={React.useMemo(() => ({ assessmentID: assessment.id }), [assessment.id])}
              loadedUsers={React.useMemo(
                () => [
                  {
                    group: true,
                    groupID: 'all',
                    name:
                      assessment.oneStudent && assessment.processed.length === 1
                        ? `1 document concerning ${assessment.processed[0].name}`
                        : `All ${assessment.processed.length} processed documents`,
                  },
                ],
                [assessment.oneStudent, assessment.processed]
              )}
              url="/frontend_api/searchAssessmentRecipients"
              userDescriptor="students"
              placeholder="Choose whose documents to send"
            />
            <div className="flex flex-row items-center gap-2">
              Send to:
              <Button
                shape={ButtonShape.roundedMedium}
                buttonStyle={sendToStudents ? ButtonStyle.blueLight : ButtonStyle.light}
                onClick={() => setSendToStudents((s) => !s)}
              >
                Students
              </Button>
              <Button
                shape={ButtonShape.roundedMedium}
                buttonStyle={sendToGuardians ? ButtonStyle.blueLight : ButtonStyle.light}
                onClick={() => setSendToGuardians((s) => !s)}
              >
                Guardians
              </Button>
            </div>
            {recipients.length > 0 && (sendToStudents || sendToGuardians) && (
              <div
                className="cursor-pointer text-sm italic text-blue-600 hover:underline"
                onClick={previewAssessmentMessage}
              >
                Preview to {getRealRecipientCount(recipients, sendToStudents, sendToGuardians)} recipients
              </div>
            )}
            <Button
              logo
              className="self-start"
              disabled={
                recipients.length === 0 || loading || !!sendDisabledMessage || (!sendToStudents && !sendToGuardians)
              }
              onClick={() => {
                setLoading(true)
                makeRequest<string | undefined>('/frontend_api/sendAssessment', {
                  assessmentID: assessment.id,
                  recipients: recipients.map((r) => (r.group ? r.groupID : r.id)),
                  sendToStudents,
                  sendToGuardians,
                })
                  .then((info) => {
                    setSendInfo(info)
                    setRecipients([])
                    reloadSends()
                  })
                  .catch(showAxiosErrorAsToast)
                  .finally(() => setLoading(false))
              }}
            >
              Send Documents
            </Button>
            {sendInfo && <div className="max-w-[350px] rounded bg-warning p-2">{sendInfo}</div>}
          </div>
        </Tooltip>
        {data && (
          <DonutChart
            className="mt-16 max-w-[350px]"
            title="Send Stats (Per Student)"
            legend={{ side: 'left' }}
            data={data.sendStats}
          />
        )}
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-row items-center justify-between">
          <b className="self-center text-xl">All Past Document Sends</b>
          <TextInput value={search} onChange={setSearch} placeholder="Search" />
        </div>
        {sends && (
          <LocalTable
            exportName={`${assessment.name} Send History`}
            search={search}
            columns={[
              {
                header: 'Name',
                accessorFn: (row) => `${row.name}${row.name == row.forStudent ? '' : ` (${row.forStudent})`}`,
              },
              {
                header: 'Date',
                accessorKey: 'date',
                cell: ({ row }) => (
                  <div className="flex items-center gap-1">
                    <DateWrapper detailed date={row.original.date} />
                    {row.original.resend ? <IconReload tooltip="This was a resend" opacity={0.5} /> : null}
                  </div>
                ),
              },
              {
                header: 'Views',
                accessorKey: 'views',
              },
              {
                header: 'Failed Attempts',
                accessorKey: 'failedAttempts',
              },
              {
                header: 'Resend',
                cell: ({ row }) => <ResendButton send={row.original} reloadSends={reloadSends} />,
              },
            ]}
            data={sends}
            defaultSort={[{ id: 'date', desc: true }]}
            pagination={10}
          />
        )}
      </div>
    </div>
  )
}

const ResendButton: React.FC<{
  send: AssessmentSend
  reloadSends: () => void
}> = ({ send, reloadSends }) => {
  const [loading, setLoading] = React.useState(false)
  return (
    <Button
      logo
      showLoader={loading}
      disabled={loading}
      onClick={() => {
        setLoading(true)
        makeRequest('/frontend_api/resendAssessment', {
          inviteID: send.id,
        })
          .then(reloadSends)
          .catch(showAxiosErrorAsToast)
          .finally(() => setLoading(false))
      }}
    >
      Resend
    </Button>
  )
}
